import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import {
	editCategoryRestaurantPropertiesInitialState,
	EditCategoryRestaurantPropertiesContext,
	TTypeOfModalWindow,
} from './editCategoryRestaurantPropertiesContext';
import { IAppContext, AppContext } from 'appState/appContext';
import { editRestaurantPropertiesReducer } from './editCategoryRestaurantPropertiesReducer';
import { EditCategoryRestaurantPropertiesActionTypes } from './editCategoryRestaurantPropertiesActions';
import {
	UserRoles,
	IGetCategoryInfoV2,
	IUpdateCategoryRestaurantProperties,
	IGetCategoryRestaurantProperties,
	IGetCategoryRestaurantPropertiesArguments,
} from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { getCategoryInfoV2, getCategoryRestaurantProperties } from 'backend/categories';
import { useFetchData } from 'hooks/useFetchData';

export const EditCategoryRestaurantPropertiesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editRestaurantPropertiesReducer, editCategoryRestaurantPropertiesInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const { response: categoryInfoResponse, loading: categoryInfoLoading } = useFetchData<IGetCategoryInfoV2, string>(
		categoryID ? getCategoryInfoV2 : null,
		categoryID || '',
	);

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetCategoryRestaurantProperties, IGetCategoryRestaurantPropertiesArguments>(
		state.IDSelectedRestaurant ? getCategoryRestaurantProperties : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			categoryId: state.categoryId,
		},
	);

	useEffect(() => {
		categoryInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [categoryInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (categoryInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const fetchedRestaurants = hasAccess
				? categoryInfoResponse.data.restaurants
				: categoryInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: EditCategoryRestaurantPropertiesActionTypes.SET_ALL_DATA,
				payload: {
					categoryId: categoryInfoResponse.data.id,
					categoryName: categoryInfoResponse.data.name || '',
					customerName: categoryInfoResponse.data.customer.name || '',
					hasSchedulePriority: categoryInfoResponse.data.overrideScheduleAvailability,
					activationSchedule: categoryInfoResponse.data.activationCron || '',
					deactivationSchedule: categoryInfoResponse.data.deactivationCron || '',
					fetchedRestaurants,
				},
			});
		}
	}, [categoryID, loggedUser.restaurants, hasAccess, categoryInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: EditCategoryRestaurantPropertiesActionTypes.SET_SUBCATEGORIES,
				payload: {
					subcategories: restaurantPropertiesResponse.data.subCategories.map((subcategory) => ({
						id: subcategory.id,
						name: subcategory.name || '',
					})),
					activationSchedule: restaurantPropertiesResponse.data.activationCron || '',
					deactivationSchedule: restaurantPropertiesResponse.data.deactivationCron || '',
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchRestaurantPropertiesData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: EditCategoryRestaurantPropertiesActionTypes.REFETCH_RESTAURANT_PROPERTIES_DATA,
				payload: {
					needRefetchRestaurantPropertiesData: false,
				},
			});
		}
	}, [state.needRefetchRestaurantPropertiesData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.IDSelectedRestaurant]);

	const refetchRestaurantPropertiesData = useCallback((value: boolean) => {
		dispatch({
			type: EditCategoryRestaurantPropertiesActionTypes.REFETCH_RESTAURANT_PROPERTIES_DATA,
			payload: {
				needRefetchRestaurantPropertiesData: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: EditCategoryRestaurantPropertiesActionTypes.SET_ID_RESTAURANT,
				payload: {
					IDSelectedRestaurant: value,
					subcategories: value ? state.subcategories : [],
					activationSchedule: value ? state.activationSchedule : '',
					deactivationSchedule: value ? state.deactivationSchedule : '',
				},
			});
		},
		[state.subcategories, state.activationSchedule, state.deactivationSchedule],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryRestaurantPropertiesActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryRestaurantPropertiesActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateCategoryRestaurantProperties>) => {
		dispatch({
			type: EditCategoryRestaurantPropertiesActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditCategoryRestaurantPropertiesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditCategoryRestaurantPropertiesContext.Provider
			value={{
				refetchRestaurantPropertiesData,
				setIDRestaurant,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchRestaurantPropertiesData: state.needRefetchRestaurantPropertiesData,
				customerName: state.customerName,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				categoryId: state.categoryId,
				categoryName: state.categoryName,
				subcategories: state.subcategories,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditCategoryRestaurantPropertiesContext.Provider>
	);
};
